import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Alert, AlertDescription } from "../../ui/alert";
import { SkeletonCard, SkeletonTable, SkeletonStats } from '../../ui/skeleton';
import {
  Users, TrendingUp, Award, AlertTriangle, Download,
  BarChart3, Target, CheckCircle, XCircle, Minus
} from 'lucide-react';
import { VictoryChart, VictoryBar, VictoryAxis, VictoryRadar, VictoryPolarAxis, VictoryTheme, VictoryLabel } from 'victory';

const ComparacaoCandidatos = () => {
  const [resultados, setResultados] = useState([]);
  const [candidatosSelecionados, setCandidatosSelecionados] = useState([]);
  const [comparacao, setComparacao] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [criterioOrdenacao, setCriterioOrdenacao] = useState('ifc_geral');
  const [incluirIA, setIncluirIA] = useState(true);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    carregarResultados();
  }, []);

  const carregarResultados = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${API}/api/fit-cultural/questionario/resultados`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        console.error('Erro na resposta:', response.status);
        throw new Error('Erro ao carregar resultados');
      }

      const data = await response.json();
      console.log('Resultados carregados:', data);
      setResultados(data || []);
    } catch (err) {
      console.error('Erro:', err);
      setError('Não foi possível carregar os resultados. Certifique-se de que existem avaliações finalizadas.');
      setResultados([]); // Define array vazio para evitar erros
    } finally {
      setLoading(false);
    }
  };

  const toggleCandidato = (funcionarioId) => {
    if (!funcionarioId) return;
    
    if (candidatosSelecionados.includes(funcionarioId)) {
      setCandidatosSelecionados(candidatosSelecionados.filter(id => id !== funcionarioId));
    } else {
      if (candidatosSelecionados.length >= 10) {
        setError('Máximo de 10 candidatos para comparação');
        return;
      }
      setCandidatosSelecionados([...candidatosSelecionados, funcionarioId]);
    }
  };

  const realizarComparacao = async () => {
    if (candidatosSelecionados.length < 2) {
      setError('Selecione pelo menos 2 candidatos para comparar');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API}/api/fit-cultural/comparar-candidatos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          candidatos_ids: candidatosSelecionados,
          criterio_ordenacao: criterioOrdenacao,
          incluir_recomendacoes_ia: incluirIA
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erro ao comparar candidatos');
      }

      const data = await response.json();
      setComparacao(data);
    } catch (err) {
      console.error('Erro:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportarPDF = () => {
    // Placeholder para exportação PDF
    alert('Funcionalidade de exportação PDF em desenvolvimento');
  };

  const exportarExcel = () => {
    // Placeholder para exportação Excel
    alert('Funcionalidade de exportação Excel em desenvolvimento');
  };

  const getStatusBadge = (statusFit) => {
    const config = {
      'Fit Alto': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'Fit Médio': { color: 'bg-yellow-100 text-yellow-800', icon: Minus },
      'Fit Baixo': { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    
    const { color, icon: Icon } = config[statusFit] || config['Fit Médio'];
    
    return (
      <Badge className={color}>
        <Icon className="w-3 h-3 mr-1" />
        {statusFit}
      </Badge>
    );
  };

  // Preparar dados para gráfico radar
  const prepararDadosRadar = () => {
    if (!comparacao || !comparacao.comparacao_dimensoes) return [];

    const dimensoes = Object.keys(comparacao.comparacao_dimensoes);
    return comparacao.candidatos.map(candidato => ({
      nome: candidato.nome,
      data: dimensoes.map(dimensao => ({
        x: dimensao,
        y: candidato.scores_dimensoes[dimensao] || 0
      }))
    }));
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-8 h-8 text-blue-600" />
            Comparação de Candidatos
          </h1>
          <p className="text-gray-600 mt-1">
            Compare múltiplos candidatos lado-a-lado e identifique o melhor fit cultural
          </p>
        </div>
        
        {comparacao && (
          <div className="flex gap-2">
            <Button onClick={exportarPDF} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Exportar PDF
            </Button>
            <Button onClick={exportarExcel} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Exportar Excel
            </Button>
          </div>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Seleção de Candidatos */}
      {!comparacao && (
        <Card>
          <CardHeader>
            <CardTitle>Selecionar Candidatos para Comparação</CardTitle>
            <CardDescription>
              Selecione de 2 a 10 candidatos que deseja comparar (Selecionados: {candidatosSelecionados.length})
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Controles */}
              <div className="flex gap-4 items-center pb-4 border-b">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">Ordenar por:</label>
                  <select
                    value={criterioOrdenacao}
                    onChange={(e) => setCriterioOrdenacao(e.target.value)}
                    className="px-3 py-2 border rounded-md"
                  >
                    <option value="ifc_geral">IFC Geral</option>
                    <option value="nome">Nome</option>
                    <option value="data_avaliacao">Data Avaliação</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="incluirIA"
                    checked={incluirIA}
                    onChange={(e) => setIncluirIA(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <label htmlFor="incluirIA" className="text-sm">
                    Incluir Recomendações de IA
                  </label>
                </div>

                <Button
                  onClick={realizarComparacao}
                  disabled={candidatosSelecionados.length < 2 || loading}
                  className="ml-auto"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {loading ? 'Comparando...' : 'Comparar Selecionados'}
                </Button>
              </div>

              {/* Lista de Candidatos */}
              {loading ? (
                <div className="grid gap-3">
                  <SkeletonTable rows={5} />
                </div>
              ) : (
                <div className="grid gap-3 max-h-96 overflow-y-auto">
                  {resultados.map((resultado) => (
                  <div
                    key={resultado.sessao_id}
                    onClick={() => toggleCandidato(resultado.funcionario_id)}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      candidatosSelecionados.includes(resultado.funcionario_id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={candidatosSelecionados.includes(resultado.funcionario_id)}
                          onChange={() => {}}
                          className="w-5 h-5"
                        />
                        <div>
                          <p className="font-semibold text-gray-900">
                            {resultado.funcionario_nome}
                          </p>
                          <p className="text-sm text-gray-600">
                            {resultado.avaliacao_nome}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">
                            {resultado.score_final?.toFixed(1) || 0}%
                          </p>
                          <p className="text-xs text-gray-500">IFC Geral</p>
                        </div>
                        {getStatusBadge(
                          resultado.score_final >= 80 ? 'Fit Alto' :
                          resultado.score_final >= 60 ? 'Fit Médio' : 'Fit Baixo'
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading da Comparação */}
      {loading && candidatosSelecionados.length >= 2 && (
        <div className="space-y-6">
          <SkeletonStats />
          <SkeletonCard />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      )}

      {/* Resultados da Comparação */}
      {!loading && comparacao && (
        <div className="space-y-6">
          {/* Botão Voltar */}
          <Button
            onClick={() => {
              setComparacao(null);
              setCandidatosSelecionados([]);
            }}
            variant="outline"
          >
            ← Nova Comparação
          </Button>

          {/* Cards de Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Melhor Candidato
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {comparacao.melhor_candidato.nome}
                    </p>
                    <p className="text-sm text-gray-600">
                      {comparacao.melhor_candidato.ifc.toFixed(1)}% IFC
                    </p>
                  </div>
                  <Award className="w-12 h-12 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Candidatos Comparados
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <p className="text-4xl font-bold text-blue-600">
                    {comparacao.total_candidatos}
                  </p>
                  <Users className="w-12 h-12 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Diferenças Significativas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <p className="text-4xl font-bold text-orange-600">
                    {comparacao.diferencas_significativas.length}
                  </p>
                  <TrendingUp className="w-12 h-12 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Ranking */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Ranking de Candidatos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {comparacao.ranking.map((item, idx) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-lg ${
                        idx === 0 ? 'bg-yellow-100 text-yellow-800' :
                        idx === 1 ? 'bg-gray-100 text-gray-800' :
                        idx === 2 ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-50 text-blue-800'
                      }`}>
                        {item.posicao}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{item.nome}</p>
                        {getStatusBadge(item.status_fit)}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-600">
                        {item.ifc.toFixed(1)}%
                      </p>
                      <p className="text-xs text-gray-500">IFC Geral</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Comparação Detalhada */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tabela Comparativa */}
            <Card>
              <CardHeader>
                <CardTitle>Comparação Detalhada</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 font-semibold">Candidato</th>
                        <th className="text-center py-2 font-semibold">IFC</th>
                        <th className="text-center py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparacao.candidatos.map((candidato) => (
                        <tr key={candidato.id} className="border-b">
                          <td className="py-3">
                            <p className="font-medium">{candidato.nome}</p>
                            <p className="text-xs text-gray-500">{candidato.cargo || 'N/A'}</p>
                          </td>
                          <td className="text-center">
                            <span className="font-bold text-blue-600">
                              {candidato.ifc_geral.toFixed(1)}%
                            </span>
                          </td>
                          <td className="text-center">
                            {getStatusBadge(candidato.status_fit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Forças e Fraquezas */}
            <Card>
              <CardHeader>
                <CardTitle>Análise Qualitativa</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-80 overflow-y-auto">
                  {comparacao.candidatos.map((candidato) => (
                    <div key={candidato.id} className="border-b pb-4 last:border-0">
                      <p className="font-semibold text-gray-900 mb-2">{candidato.nome}</p>
                      
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs font-medium text-green-700 mb-1">Forças:</p>
                          <ul className="text-xs text-gray-600 space-y-1">
                            {candidato.forcas.slice(0, 2).map((forca, idx) => (
                              <li key={idx}>• {forca}</li>
                            ))}
                          </ul>
                        </div>
                        
                        {candidato.fraquezas.length > 0 && candidato.fraquezas[0] !== "Dados insuficientes" && (
                          <div>
                            <p className="text-xs font-medium text-red-700 mb-1">Fraquezas:</p>
                            <ul className="text-xs text-gray-600 space-y-1">
                              {candidato.fraquezas.slice(0, 2).map((fraqueza, idx) => (
                                <li key={idx}>• {fraqueza}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Gráfico de Comparação por Dimensões */}
          {Object.keys(comparacao.comparacao_dimensoes || {}).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Comparação por Dimensões</CardTitle>
                <CardDescription>
                  Análise detalhada de scores por dimensão cultural
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <VictoryChart
                    theme={VictoryTheme.material}
                    domainPadding={20}
                    height={300}
                  >
                    <VictoryAxis
                      tickLabelComponent={<VictoryLabel angle={-45} textAnchor="end" />}
                    />
                    <VictoryAxis dependentAxis tickFormat={(t) => `${t}%`} />
                    {Object.entries(comparacao.comparacao_dimensoes).map(([dimensao, dados], idx) => (
                      <VictoryBar
                        key={dimensao}
                        data={dados.map((d, i) => ({ x: `${d.nome.substring(0, 15)}...`, y: d.score, label: `${d.score.toFixed(1)}%` }))}
                        style={{
                          data: { fill: `hsl(${idx * 60}, 70%, 50%)` }
                        }}
                      />
                    ))}
                  </VictoryChart>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Diferenças Significativas */}
          {comparacao.diferencas_significativas.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Diferenças Significativas (Gaps {">"} 15%)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {comparacao.diferencas_significativas.map((diff, idx) => (
                    <div key={idx} className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="font-semibold text-gray-900 mb-2">{diff.dimensao}</p>
                      <div className="flex justify-between items-center text-sm">
                        <div>
                          <p className="text-green-700">✓ {diff.melhor}: {diff.melhor_score.toFixed(1)}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-orange-600 font-bold">Gap: {diff.diferenca.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-red-700">✗ {diff.pior}: {diff.pior_score.toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Insights */}
          {comparacao.insights.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Insights Automáticos</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {comparacao.insights.map((insight, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{insight}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Recomendações de IA */}
          {comparacao.recomendacoes_ia.length > 0 && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <Target className="w-5 h-5" />
                  Recomendações Inteligentes
                </CardTitle>
                <CardDescription>
                  Análise gerada por Inteligência Artificial
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {comparacao.recomendacoes_ia.map((recomendacao, idx) => (
                    <li key={idx} className="flex items-start gap-2 p-3 bg-white rounded-lg">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </div>
                      <span className="text-gray-700">{recomendacao}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ComparacaoCandidatos;
